<script lang="ts" setup>
import Switch from "@/components/Reusables/Switches/Switch.vue";

type Item = { id?: string | number; label: string; value: number | string };

const props = withDefaults(
  defineProps<{
    headers: {
      label: string;
      value: string;
    };
    items: Item[];
    // When provided, a per-row Switch is rendered. The map is keyed by item.id.
    enabledMap?: Record<string, boolean>;
    /**
     * Bumped by the caller when it rewrites `enabledMap` itself rather than in response to a row's
     * own toggle — the master switch's select-all / deselect-all, or a fresh read from the server.
     *
     * The shared Switch renders from the DOM and only reads its `value` prop on mount, so a row
     * whose value moved underneath it needs a fresh instance to show it. Keying on the value itself
     * would do that too, but it would also remount the row the reader just clicked and drop their
     * keyboard focus with it.
     */
    resyncKey?: number;
  }>(),
  { resyncKey: 0 }
);

const emit = defineEmits<{
  toggleItem: [id: string, enabled: boolean];
}>();

const showToggle = (): boolean => props.enabledMap !== undefined;

const isItemEnabled = (item: Item): boolean => {
  if (!props.enabledMap || item.id === undefined) return true;
  return props.enabledMap[String(item.id)] ?? true;
};
</script>

<template>
  <div class="flex flex-col">
    <div class="flex gap-4 pb-1">
      <p class="t-xs text-type-muted">{{ headers.label }}</p>
      <p class="t-xs text-type-muted ml-auto">{{ headers.value }}</p>
    </div>
    <div
      v-for="(item, i) in items"
      :key="item.id ?? i"
      class="flex items-center gap-4 py-2"
      :class="{ 'border-t border-slate-200': i > 0 }">
      <p class="t-small text-type-default grow">{{ item.label }}</p>
      <p class="t-small text-type-default shrink-0 tabular-nums">{{ item.value }}</p>
      <Switch
        v-if="showToggle() && item.id !== undefined"
        :key="`toggle-${item.id}-${resyncKey}`"
        color="primary"
        :value="isItemEnabled(item)"
        :aria-label="`Use standard value for ${item.label}`"
        @update:value="(val) => emit('toggleItem', String(item.id), val)" />
    </div>
  </div>
</template>
